/**
 * Waiting on you on the signed-in home (web-account-pages D5, D12, signed-in-home.html
 * Band 4, complete-booking.html): the dashboard's one list, so every site agrees on
 * what waits. Bookings the studio made that wait on the client come first, soonest
 * deadline first; then the forms the studio sent, approved requests still to pay,
 * and the studio's unread messages. Forms follow Gabriel's ruling on every device
 * (2026-10-22): one or two are a row each; three or more are one summary,
 * "N forms to fill out", whose View forms opens it in place into a row per form,
 * and Show less folds it back.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'waiting-on-you',
  description:
    "What the client still has to do: bookings the studio made that wait on them, the studio's forms (three or more folded into one summary that opens in place), approved requests to pay, and unread messages.",
  placeable: true,
  options: {},
  parts: {
    section: { description: 'The whole Waiting on you.', required: false },
    heading: { description: 'Waiting on you.', required: false, role: 'title' },
    head: { description: 'The block around the heading.', required: false },
    row: { description: 'One row holding a held booking, a form or a payment; the link inside names which.', required: false },
    icon: { description: "A row's icon, by what waits.", required: false },
    held: { description: 'A booking the studio made that waits on the client: its date and deadline; opens what is left (Complete booking or Pay).', required: true },
    form: { description: 'One form: its name and when it was sent; opens the form.', required: true },
    formsSummary: { description: 'Three or more forms: N forms to fill out, and when the oldest was sent.', required: false },
    viewForms: { description: 'View forms: opens the summary into a row per form.', required: true },
    showLess: { description: 'Show less: folds the forms back into the summary.', required: false },
    payment: { description: 'An approved request still to pay: opens Pay now.', required: true },
    messages: { description: 'New messages from the studio.', required: false },
    readMessages: { description: 'Read: opens the messages.', required: false },
  },
  actions: {
    openForms: { description: 'Opens the forms summary into a row per form.', args: [] },
    closeForms: { description: 'Folds the forms back into the summary.', args: [] },
    readMessages: { description: 'Opens the messages panel.', args: [] },
    open: { description: 'Opens a page of the site (a form, Complete booking, Pay now).', args: [{ name: 'href', kind: 'value', type: 'string', format: 'path' }] },
  },
  events: {},
});

/** A booking the studio made that waits on the client. */
export interface WaitingHeld {
  /** The service's or event's name. */
  readonly name: string;
  /** The session's date, "Sat, Oct 24". */
  readonly date: string;
  /** The deadline, "Sat at 6:00 PM". */
  readonly finishBy: string;
  /** What is left, the Telegram message's own button: Complete booking, Complete Intake, Register or Pay $140. */
  readonly action: string;
  /** The booking's Complete step, or its pay page when only payment is left. */
  readonly href: string;
}

/** One waiting form. */
export interface WaitingForm {
  /** The form's name. */
  readonly name: string;
  /** "sent Oct 14". */
  readonly sent: string;
  /** The form on the website, `/form-request/<requestToken>`. */
  readonly href: string;
}

/** An approved request still to pay. */
export interface WaitingPayment {
  /** The service's name. */
  readonly name: string;
  /** Pay now for that booking. */
  readonly href: string;
}

/** What Waiting on you knows. */
export interface WaitingOnYouState {
  /** Bookings the studio made that wait on the client, soonest deadline first. */
  readonly held: readonly WaitingHeld[];
  /** The forms as listed: every form for one or two, or for three or more once opened; none while folded. */
  readonly forms: readonly WaitingForm[];
  /** Three or more forms: their count and when the oldest was sent; null for one or two. */
  readonly summary: { readonly count: number; readonly oldestSent: string } | null;
  /** True while the summary is open. */
  readonly formsOpen: boolean;
  /** Approved requests still to pay. */
  readonly payments: readonly WaitingPayment[];
  /** "2 new messages", or null when nothing is unread. */
  readonly messages: string | null;
  /** The studio's name, for the messages line. */
  readonly studioName: string;
  /** True when nothing is waiting: the section is left out. */
  readonly empty: boolean;
}

/** What a drawing can ask Waiting on you to do. */
export interface WaitingOnYouActions {
  /** Open the forms summary into a row per form. */
  readonly openForms: () => void;
  /** Fold the forms back into the summary. */
  readonly closeForms: () => void;
  /** Open the messages panel. */
  readonly readMessages: () => void;
  /** Open a page of the site. */
  readonly open: (href: string) => void;
}

/** Waiting on you tells its host nothing. */
export type WaitingOnYouEvents = Record<string, never>;
